import _ from 'lodash'
import { useMemo } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Consultation } from '@/core/models/consultation.model'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import {
  searchParticipants,
  selectConsultationInviteRequestTracker,
  selectSearchParticipantsResult,
  sendInvite,
  updateConsultation,
} from '@/redux/reducers/consultation.reducer'

import InvitationDialog, { Participant } from '../components/InvitationDialog'

export type InvitationDialogContainerProps = {
  consultation?: Consultation
  roomId?: string
  open: boolean
  onClose: () => void
}

export function InvitationDialogContainer(props: InvitationDialogContainerProps) {
  const { consultation, open, onClose } = props
  const dispatch = useAppDispatch()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const { items: searchedParticipants } = useAppSelector(selectSearchParticipantsResult)

  const participants = useMemo(() => consultation?.participants ?? [], [consultation])

  const suggestedParticipants: Participant[] = useMemo(() => {
    return _.map(searchedParticipants, (participant) => ({
      email: participant.email,
      name: participant.name,
      surname: participant.surname,
      type: participant.type,
    }))
  }, [searchedParticipants])

  const sendInviteRequestHandler = useRequestHandler({
    onSuccess: () => {
      snackbar.enqueueSnackbar(t('notification.consultation.invite.success'), { variant: 'success' })
    },
    onError: () => {
      snackbar.enqueueSnackbar(t('notification.consultation.invite.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectConsultationInviteRequestTracker,
  })

  const handleRemoveParticipant = (removedParticipant: Participant) => {
    if (!consultation) return

    dispatch(updateConsultation(consultation._id, { participants: participants.filter((participant) => participant.email !== removedParticipant.email) }))
  }

  const handleSearchParticipant = (searchTerm: string, participantType: string) => {
    if (!consultation) return

    dispatch(searchParticipants(searchTerm, participantType.toUpperCase(), consultation._id))
  }

  const handleSelectParticipant = (participant: Participant) => {
    if (consultation) {
      sendInviteRequestHandler.dispatch(sendInvite(consultation._id, participant))
    }

    // TODO: handle emergency room invitation
    // if (roomId) {
    //   sendInviteRequestHandler.dispatch(sendEmergencyRoomInvitation(roomId, participant))
    // }
  }

  return (
    <InvitationDialog
      onClose={onClose}
      onRemoveParticipant={handleRemoveParticipant}
      onSearchParticipant={handleSearchParticipant}
      onSelectParticipant={handleSelectParticipant}
      open={open}
      participants={participants}
      suggestedParticipants={suggestedParticipants}
    />
  )
}

export default InvitationDialogContainer
